import { NetworkTraceObject } from '@barthauer/abc/core/data';
import { DataEntity, ObjectTypesHelper } from '..';

interface EdgeData {
  Id: string;
  ObjectType: string | number;
  StartNodeId: string;
  EndNodeId: string;
  PreviousIn: number[];
  PreviousRev: number[];
  NextIn: number[];
  NextRev: number[];
  CompareValue: any;
}

class MapNetResultEdge {
  public nextEdge: MapNetResultEdge;
  public isEnd: boolean;

  constructor(
    private readonly tracer: MapNetTracer,
    public readonly edge: EdgeData,
    private readonly previous: MapNetResultEdge[]
  ) {}

  public get id(): string {
    return this.edge.Id;
  }

  public get mainId(): string {
    return MapNetResultEdge.getMainId(this.id);
  }

  public static getMainId(id: string): string {
    return id?.split('|')[0];
  }

  public get objectType(): string | number {
    return this.edge.ObjectType;
  }

  public get startId(): string {
    return this.edge.StartNodeId;
  }

  public get endId(): string {
    return this.edge.EndNodeId;
  }

  public get isInFlowDirection(): boolean {
    if (!this.nextEdge) {
      if (this.previous?.length) {
        return !MapNetResultEdge.getFlowDirectionOfPrevious(this, this.previous[this.previous.length - 1]);
      } else {
        return true;
      }
    } else {
      return MapNetResultEdge.getFlowDirectionOfPrevious(this, this.nextEdge);
    }
  }

  private static getFlowDirectionOfPrevious(previous: MapNetResultEdge, next: MapNetResultEdge): boolean {
    if (previous.startId && (previous.startId === next.startId || previous.startId === next.endId)) {
      return false;
    } else if (previous.endId && (previous.endId === next.startId || previous.endId === next.endId)) {
      return true;
    } else {
      return undefined;
    }
  }

  public clonePrevious(): MapNetResultEdge[] {
    if (!this.previous) {
      return [];
    }
    return [...this.previous];
  }

  public get isStarting(): boolean {
    return !this.edge.PreviousIn?.length && !this.edge.PreviousRev?.length;
  }

  public get isEnding(): boolean {
    return !this.edge.NextIn?.length && !this.edge.NextRev?.length;
  }
}

export class MapNetTracer {
  constructor(
    private edgeObjectTypeName: string,
    private loadNetworkItem: (key: string) => Promise<any>,
    private getObjectTypeName: (ot: string | number) => Promise<string>
  ) {}

  public async search(
    startEdgeIds: string[],
    startNodeIds: string[],
    endEdgeIds: string[],
    endNodeIds: string[],
    shortestPathOnly: boolean,
    fromStartToEnd: boolean = shortestPathOnly,
    fromEndToStart: boolean = shortestPathOnly,
    mustEnd: boolean = shortestPathOnly,
    maxDepth: number = 1000
  ): Promise<NetworkTraceObject[]> {
    let nodeStartEdgeStartingIds: string[] = [];
    let nodeStartEdgeEndingIds: string[] = [];
    let nodeEndEdgeStartingIds: string[] = [];
    let nodeEndEdgeEndingIds: string[] = [];

    if (startNodeIds?.length) {
      let ids = (nodeStartEdgeStartingIds = await this.findEdgeIdsByNodes(startNodeIds, true));
      if (!ids?.length) {
        ids = nodeStartEdgeEndingIds = await this.findEdgeIdsByNodes(startNodeIds, false);
      }
      if (ids) {
        startEdgeIds = startEdgeIds?.map((_) => _) ?? [];
        startEdgeIds.push(...ids);
      }
    }
    if (endNodeIds?.length) {
      let ids = (nodeEndEdgeEndingIds = await this.findEdgeIdsByNodes(endNodeIds, false));
      if (!ids?.length) {
        ids = nodeEndEdgeStartingIds = await this.findEdgeIdsByNodes(endNodeIds, true);
      }
      if (ids) {
        endEdgeIds = endEdgeIds?.map((_) => _) ?? [];
        endEdgeIds.push(...ids);
      }
    }

    const startEdges: MapNetResultEdge[] = [];
    const endEdges: MapNetResultEdge[] = [];
    const initialEdgesStartToEnd: MapNetResultEdge[] = [];
    const initialEdgesEndToStart: MapNetResultEdge[] = [];
    let oneWayOnly = false;

    if (fromStartToEnd && fromEndToStart) {
      if (startEdgeIds?.length) {
        for (const edgeId of startEdgeIds) {
          const edges = await this.findEdgeDataByEdge(edgeId);
          for (const edge of edges) {
            startEdges.push(this.createResultEdge(edge));
          }
          initialEdgesStartToEnd.push(...edges.map((_) => this.createResultEdge(_)));
          if (!endEdgeIds?.length) {
            initialEdgesEndToStart.push(...edges.map((_) => this.createResultEdge(_)));
          }
        }
      }
      if (endEdgeIds?.length) {
        for (const edgeId of endEdgeIds) {
          const edges = await this.findEdgeDataByEdge(edgeId);
          for (const edge of edges) {
            endEdges.push(this.createResultEdge(edge));
          }
          initialEdgesEndToStart.push(...edges.map((_) => this.createResultEdge(_)));
          if (!startEdgeIds?.length) {
            initialEdgesStartToEnd.push(...edges.map((_) => this.createResultEdge(_)));
          }
        }
      }
    } else {
      oneWayOnly = true;
      if (fromStartToEnd) {
        if (!startEdgeIds?.length) {
          startEdgeIds = endEdgeIds;
          endEdgeIds = [];
        }
        if (!endEdgeIds?.length) {
          endEdgeIds = startEdgeIds;
          startEdgeIds = [];
        }
      }
      for (const edgeId of startEdgeIds) {
        const edges = await this.findEdgeDataByEdge(edgeId);
        for (const edge of edges) {
          startEdges.push(this.createResultEdge(edge));
        }
        initialEdgesStartToEnd.push(...edges.map((_) => this.createResultEdge(_)));
      }
      for (const edgeId of endEdgeIds) {
        const edges = await this.findEdgeDataByEdge(edgeId);
        for (const edge of edges) {
          endEdges.push(this.createResultEdge(edge));
        }
        initialEdgesEndToStart.push(...edges.map((_) => this.createResultEdge(_)));
      }
    }

    let lastEdges: MapNetResultEdge[] = [];
    const allFoundEdges = new Map<string, MapNetResultEdge>();
    let found = await this.searchCore(
      startEdges,
      endEdges,
      oneWayOnly,
      shortestPathOnly,
      maxDepth,
      true,
      allFoundEdges,
      initialEdgesStartToEnd,
      lastEdges
    );
    if ((!found || !shortestPathOnly) && initialEdgesEndToStart?.length) {
      const lastEdges2: MapNetResultEdge[] = [];
      const found2 = await this.searchCore(
        startEdges,
        endEdges,
        oneWayOnly,
        shortestPathOnly,
        maxDepth,
        false,
        allFoundEdges,
        initialEdgesEndToStart,
        lastEdges2
      );
      found = found || found2;
      if (!lastEdges?.length) {
        lastEdges = lastEdges2;
      }
    }

    let result: MapNetResultEdge[] = [];
    if (found) {
      if (shortestPathOnly) {
        let edge: MapNetResultEdge;
        for (const lastEdge of lastEdges) {
          if (lastEdge.isEnd) {
            edge = lastEdge;
          }
        }
        if (edge) {
          result.push(edge);

          const previous = edge.clonePrevious();
          for (let i = previous.length - 1; i >= 0; i--) {
            const pe = previous[i];
            if (!this.isInEdges([pe], result)) {
              result.splice(0, 0, pe);
            }
            pe.nextEdge = edge;
            edge = pe;
          }
        }
      } else if (mustEnd) {
        for (const e of Array.from(allFoundEdges)) {
          const edge = e[1];
          if (edge.isEnd) {
            if (!this.isInEdges([edge], result)) {
              result.push(edge);
            }

            const previous = edge.clonePrevious();
            for (const pe of previous) {
              pe.isEnd = true;
              if (!this.isInEdges([pe], result)) {
                result.push(pe);
              }
            }
          }
        }
      } else {
        for (const e of Array.from(allFoundEdges)) {
          result.push(e[1]);
        }
      }
    } else if (shortestPathOnly) {
      if (startEdges?.length) {
        result.push(startEdges[0]);
      } else if (endEdges?.length) {
        result.push(endEdges[0]);
      } else {
        for (const e of Array.from(allFoundEdges)) {
          result.push(e[1]);
        }
      }
    } else {
      for (const edge of startEdges) {
        result.push(edge);
      }
      for (const edge of endEdges) {
        result.push(edge);
      }
      for (const e of Array.from(allFoundEdges)) {
        result.push(e[1]);
      }
    }

    let checkEdges = true;
    const startNodeId: string = startNodeIds?.length === 1 ? startNodeIds[0] : null;
    const endNodeId: string = endNodeIds?.length === 1 ? endNodeIds[0] : null;
    const startEdgeId: string = startEdgeIds?.length === 1 ? startEdgeIds[0] : null;
    const endEdgeId: string = endEdgeIds?.length === 1 ? endEdgeIds[0] : null;
    if ((startNodeId || startEdgeId) && (endNodeId || endEdgeId)) {
      let startFoundCounter = 0;
      let endFoundCounter = 0;
      if (startNodeId && result.find((_) => _.startId === startNodeId || _.endId === startNodeId)) {
        startFoundCounter++;
      }
      if (endNodeId && result.find((_) => _.startId === endNodeId || _.endId === endNodeId)) {
        endFoundCounter++;
      }
      if (startEdgeId && result.find((_) => _.mainId === startEdgeId)) {
        startFoundCounter++;
      }
      if (endEdgeId && result.find((_) => _.mainId === endEdgeId)) {
        endFoundCounter++;
      }
      if (startFoundCounter === 1 && endFoundCounter === 1) {
        const newresult: MapNetResultEdge[] = [];
        let foundFromStart = false;
        let foundFromEnd = false;
        for (const r of result) {
          const isStart =
            (startEdgeId && r.mainId === startEdgeId) ||
            (startNodeId && (r.isInFlowDirection ? r.startId === startNodeId : r.endId === startNodeId));
          const isEnd =
            (endEdgeId && r.mainId === endEdgeId) ||
            (endNodeId && (!r.isInFlowDirection ? r.startId === endNodeId : r.endId === endNodeId));

          if (!foundFromStart && !foundFromEnd) {
            foundFromStart = isStart;
            if (!foundFromStart) {
              foundFromEnd = isEnd;
            }
          }
          if (foundFromStart || foundFromEnd) {
            newresult.push(r);
          }
          if ((foundFromStart && isEnd) || (foundFromEnd && isStart)) {
            break;
          }
        }
        result = newresult;
        if (startNodeId && endNodeId) {
          checkEdges = false;
        }
      }
    }

    if (checkEdges && result.length && shortestPathOnly) {
      await this.ensureFullEdgeWithConnectedNodes(result, !startNodeIds?.length, !endNodeIds?.length);
    }

    const objects: NetworkTraceObject[] = [];
    let lastNode: DataEntity;
    for (const edge of result) {
      const isStart: boolean = edge.isStarting;
      const isEnd: boolean = edge.isEnding;
      const isStartSelection: boolean = startEdgeIds?.indexOf(edge.id) >= 0;
      const isEndSelection: boolean = endEdgeIds?.indexOf(edge.id) >= 0;
      const isInFlowDirection = edge.isInFlowDirection;
      let add = true;

      const edgeId = edge?.mainId;

      if (checkEdges) {
        if ((isStart || isStartSelection) && !isInFlowDirection && nodeStartEdgeStartingIds.indexOf(edgeId) >= 0) {
          add = false;
        } else if ((isStart || isStartSelection) && isInFlowDirection && nodeStartEdgeEndingIds.indexOf(edgeId) >= 0) {
          add = false;
        } else if ((isEnd || isEndSelection) && isInFlowDirection && nodeEndEdgeStartingIds.indexOf(edgeId) >= 0) {
          add = false;
        } else if ((isEnd || isEndSelection) && !isInFlowDirection && nodeEndEdgeEndingIds.indexOf(edgeId) >= 0) {
          add = false;
        }
      }

      if (add) {
        const o = await this.createNetworkTraceObject(edge);
        o.Id = edgeId;
        if (lastNode) {
          o.FirstNode = lastNode;
        } else {
          const nodeId: string = o.IsInFlowDirection ? edge.startId : edge.endId;
          if (nodeId) {
            o.FirstNode = new DataEntity(
              ObjectTypesHelper.getMainNodeObjectType(await this.getObjectTypeName(edge.objectType)),
              nodeId
            );
          }
        }
        const nodeId2: string = o.IsInFlowDirection ? edge.endId : edge.startId;
        if (nodeId2) {
          lastNode = new DataEntity(
            ObjectTypesHelper.getMainNodeObjectType(await this.getObjectTypeName(edge.objectType)),
            nodeId2
          );
          o.SecondNode = lastNode;
        }
        objects.push(o);
      }
    }

    return objects;
  }

  private async ensureFullEdgeWithConnectedNodes(
    result: MapNetResultEdge[],
    tryExtendAtStart: boolean,
    tryExtendAtEnd: boolean
  ): Promise<void> {
    if (tryExtendAtStart) {
      await this.ensureFullEdgeWithConnectedNodesCore(result, true);
    }
    if (tryExtendAtEnd) {
      await this.ensureFullEdgeWithConnectedNodesCore(result, false);
    }
  }

  private async ensureFullEdgeWithConnectedNodesCore(result: MapNetResultEdge[], isAtStart: boolean): Promise<void> {
    let foundEdge = isAtStart ? result[0] : result[result.length - 1];
    const foundEdgeId = foundEdge.mainId;
    let lastEdge = foundEdge;
    let found: boolean;
    do {
      found = false;
      let nodeId;
      let continueAtStartNode: boolean;
      if (isAtStart) {
        if (lastEdge.isInFlowDirection) {
          nodeId = lastEdge.startId;
          continueAtStartNode = false;
        } else {
          nodeId = lastEdge.endId;
          continueAtStartNode = true;
        }
      } else {
        if (lastEdge.isInFlowDirection) {
          nodeId = lastEdge.endId;
          continueAtStartNode = true;
        } else {
          nodeId = lastEdge.startId;
          continueAtStartNode = false;
        }
      }
      if (nodeId) {
        const edgeIds = await this.findEdgeIdsByNodes([nodeId], continueAtStartNode);
        for (const edgeId of edgeIds) {
          if (edgeId !== lastEdge.id && MapNetResultEdge.getMainId(edgeId) === foundEdgeId) {
            const edges = await this.findEdgeDataByEdge(edgeId);
            if (edges) {
              for (const edge of edges) {
                found = true;
                let newEdge: MapNetResultEdge;
                if (isAtStart) {
                  newEdge = this.createResultEdge(edge);
                  newEdge.nextEdge = lastEdge;
                  result.unshift(newEdge);
                } else {
                  newEdge = this.createResultEdge(edge, lastEdge);
                  result.push(newEdge);
                }
                lastEdge = newEdge;
              }
            }
          }
        }
      }
    } while (found);
  }

  private async createNetworkTraceObject(edge: MapNetResultEdge): Promise<NetworkTraceObject> {
    const result = new NetworkTraceObject(edge.edge);
    result.ObjectTypeName = await this.getObjectTypeName(edge.edge.ObjectType);
    result.IsInFlowDirection = edge.isInFlowDirection;
    //TODO: GeometryWKT?
    return result;
  }

  private createResultEdge(edge: EdgeData, parent?: MapNetResultEdge): MapNetResultEdge {
    let previous: MapNetResultEdge[];
    if (parent) {
      previous = parent.clonePrevious();
      previous.push(parent);
    }
    return new MapNetResultEdge(this, edge, previous);
  }

  private async searchCore(
    startEdges: MapNetResultEdge[],
    endEdges: MapNetResultEdge[],
    oneWayOnly: boolean,
    shortestPathOnly: boolean,
    maxDepth: number,
    fromStartToEnd: boolean,
    allFoundEdges: Map<string, MapNetResultEdge>,
    currentEdges: MapNetResultEdge[],
    lastEdges: MapNetResultEdge[]
  ): Promise<boolean> {
    for (const edge of currentEdges) {
      if (!allFoundEdges.get(edge.id)) {
        allFoundEdges.set(edge.id, edge);
      }
    }

    let found = false;
    let depth = 0;
    while (!(found && shortestPathOnly) && currentEdges.length && depth < maxDepth) {
      const newEdges: MapNetResultEdge[] = [];
      for (const edge of currentEdges) {
        if (
          !edge.isEnd &&
          (oneWayOnly ||
            shortestPathOnly ||
            depth === 0 ||
            (startEdges.length > 0 && !this.isInEdges([edge], startEdges)) ||
            (startEdges.length === 0 && !this.isInEdges([edge], endEdges)))
        ) {
          if (fromStartToEnd && oneWayOnly) {
            await this.addRows(allFoundEdges, newEdges, edge, edge.edge.NextIn);
          } else if (fromStartToEnd) {
            await this.addRows(allFoundEdges, newEdges, edge, edge.edge.NextIn, edge.edge.NextRev);
          } else if (!fromStartToEnd && oneWayOnly) {
            await this.addRows(allFoundEdges, newEdges, edge, edge.edge.PreviousRev);
          } else if (!fromStartToEnd) {
            await this.addRows(allFoundEdges, newEdges, edge, edge.edge.PreviousIn, edge.edge.PreviousRev);
          }

          if (!oneWayOnly && (depth > 0 || shortestPathOnly)) {
            if (fromStartToEnd && oneWayOnly) {
              await this.addRows(allFoundEdges, newEdges, edge, edge.edge.PreviousIn);
            } else if (fromStartToEnd) {
              await this.addRows(allFoundEdges, newEdges, edge, edge.edge.PreviousIn, edge.edge.PreviousRev);
            } else if (!fromStartToEnd && oneWayOnly) {
              await this.addRows(allFoundEdges, newEdges, edge, edge.edge.NextIn);
            } else if (!fromStartToEnd) {
              await this.addRows(allFoundEdges, newEdges, edge, edge.edge.NextIn, edge.edge.NextRev);
            }
          }

          if (fromStartToEnd && this.isInEdges(newEdges, endEdges, true)) {
            found = true;
          } else if (!fromStartToEnd && this.isInEdges(newEdges, startEdges, true)) {
            found = true;
          }
        }
      }
      currentEdges = newEdges;

      depth++;
    }

    lastEdges.splice(0, lastEdges.length);
    lastEdges.push(...currentEdges);

    return found;
  }

  private isInEdges(currentEdges: MapNetResultEdge[], candidateEdges: MapNetResultEdge[], setAsEnd?: boolean): boolean {
    let found = false;
    if (candidateEdges) {
      for (const edge of currentEdges) {
        if (candidateEdges.find((_) => _.id === edge.id && _.startId === edge.startId)) {
          found = true;
          if (setAsEnd) {
            edge.isEnd = true;
          } else {
            break;
          }
        }
      }
    }
    return found;
  }

  private async addRows(
    allFoundEdges: Map<string, MapNetResultEdge>,
    newEdges: MapNetResultEdge[],
    parent: MapNetResultEdge,
    data: number[],
    data2?: number[]
  ): Promise<void> {
    for (const arr of [data, data2]) {
      if (arr) {
        for (const id of arr) {
          const edge = (await this.loadNetworkItem(this.edgeObjectTypeName + '_' + id)) as EdgeData;
          if (edge) {
            const key = edge.Id;
            if (!allFoundEdges.get(key)) {
              if (
                !parent ||
                parent.edge.CompareValue === undefined ||
                parent.edge.CompareValue === null ||
                parent.edge.CompareValue === edge.CompareValue
              ) {
                const newEdge = this.createResultEdge(edge, parent);

                allFoundEdges.set(key, newEdge);
                newEdges.push(newEdge);
              }
            }
          }
        }
      }
    }
  }

  private async findEdgeIdsByNodes(nodeIds: string[], isStartNode: boolean): Promise<string[]> {
    const result: string[] = [];
    if (nodeIds?.length) {
      for (const nodeId of nodeIds) {
        const item = await this.loadNetworkItem(nodeId);
        if (item) {
          for (const key of item) {
            const edge = await this.loadNetworkItem(key);
            if (edge) {
              if ((isStartNode && edge['StartNodeId'] === nodeId) || (!isStartNode && edge['EndNodeId'] === nodeId)) {
                result.push(edge['Id']);
              }
            }
          }
        }
      }
    }
    return result;
  }

  private async findEdgeDataByEdge(edgeId: string): Promise<EdgeData[]> {
    const result: EdgeData[] = [];
    const item = await this.loadNetworkItem(edgeId);
    if (item) {
      for (const key of item) {
        const edge = await this.loadNetworkItem(key);
        if (edge) {
          result.push(edge);
          break;
        }
      }
    }
    return result;
  }
}
